import React, { useState, useEffect, useRef } from 'react';
import { XCircle } from 'lucide-react';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import PhoneInput from '../ui/PhoneInput';
import ConsentCheckbox from '../ui/ConsentCheckbox';
import Disclaimer from '../ui/Disclaimer';

type ContactInfoFormProps = {
  formData: FormData;
  validationErrors: {
    email: string | null;
    phone: string | null;
  };
  loading: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
};

export default function ContactInfoForm({
  formData,
  validationErrors,
  loading,
  error,
  onChange,
  onSubmit
}: ContactInfoFormProps) {
  const phoneInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the phone input when the component mounts
    phoneInputRef.current?.focus();
  }, []);

  const handlePhoneChange = (value: string) => {
    onChange({
      target: {
        name: 'phone',
        value
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const isFormValid = () => {
    return (
      formData.phone &&
      formData.smsConsent === true &&
      !validationErrors.phone
    );
  };

  return (
    <form id="contact-info-form" name="contact-info-form" onSubmit={onSubmit} className="space-y-6">
      <div className="text-center">
        <Title 
          id="contact-info-title"
          name="contact-info-title"
          as="h2" 
          className="mb-2"
        >
          Enter Your Phone Number
        </Title>
      </div>

      <div className="space-y-4">
        <PhoneInput
          ref={phoneInputRef}
          id="contact-info-phone"
          name="contact-info-phone"
          value={formData.phone || ''}
          onChange={handlePhoneChange}
          error={validationErrors.phone}
          label="Mobile Phone"
          required
        />
        
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <Button
        id="contact-info-submit"
        name="contact-info-submit"
        type="submit"
        disabled={loading || !isFormValid()}
        loading={loading}
        loadingText="Processing..."
        fullWidth
        size="lg"
      >
        Continue
      </Button>

      <Disclaimer
        id="contact-info-disclaimer"
        name="contact-info-disclaimer"
      >
        <p className="mb-4">By continuing, you agree to receive SMS messages, phone calls, and emails—potentially using automated technology or prerecorded/artificial voice—from Symple Lending and its lending partners (“<a href="https://symplelending.com/partners" target="_blank" rel="noopener noreferrer">Partners</a>") regarding application updates and account-related communications. Message/data rates may apply; frequency varies. Reply HELP for assistance or STOP to cancel.</p>
        
        <p>By continuing, you also provide Symple Lending and its{' '}<a href="https://symplelending.com/partners" target="_blank" rel="noopener noreferrer">Partners</a>{' '}express written consent to obtain your credit report for application purposes. You also agree to
        {' '}<a href="https://legal.spinwheel.io/end-user-agreement/" target="_blank" rel="noopener noreferrer">
        Spinwheel's End User Agreement
        </a>{' '}
        and authorize Spinwheel Solutions, Inc. to retrieve your credit profile from any consumer reporting agency.</p>
      </Disclaimer>
    </form>
  );
}